import * as React from "react";
import type { CSSProperties } from "react";

/**
 * Settings section shell.
 *
 * Each settings section is a fieldset with a legend for proper
 * keyboard and screen-reader semantics.
 */
export interface SettingsSectionProps {
  /** Stable DOM id for anchor links and aria relationships. */
  id: string;
  /** Visible section title. */
  title: string;
  /** Optional description rendered beneath the title. */
  description?: React.ReactNode;
  /** Section body. */
  children: React.ReactNode;
  /** Additional classnames. */
  className?: string;
}

export function SettingsSection({
  id,
  title,
  description,
  children,
  className,
}: SettingsSectionProps) {
  const legendId = `${id}-legend`;
  const descriptionId = description ? `${id}-description` : undefined;

  return (
    <fieldset
      id={id}
      aria-labelledby={legendId}
      aria-describedby={descriptionId}
      style={styles.fieldset}
      className={className}
    >
      <div style={styles.header}>
        <legend id={legendId} style={styles.legend}>
          {title}
        </legend>
        {description && (
          <p id={descriptionId} style={styles.description}>
            {description}
          </p>
        )}
      </div>
      <div style={styles.content}>{children}</div>
    </fieldset>
  );
}

/**
 * Stable id for a section's legend.
 */
export function settingsSectionLegendId(sectionId: string): string {
  return `${sectionId}-legend`;
}

/**
 * Stable id for a section's description paragraph.
 */
export function settingsSectionDescriptionId(
  sectionId: string,
  hasDescription: boolean,
): string | undefined {
  return hasDescription ? `${sectionId}-description` : undefined;
}

const styles: Record<string, CSSProperties> = {
  fieldset: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-default)",
    background: "var(--bg-primary)",
    padding: "24px",
    boxShadow: "var(--shadow-card)",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  legend: {
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  description: {
    fontSize: "14px",
    color: "var(--text-muted)",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
};
